import { useState, useRef } from 'react'
import { apiClient } from '../../services/api'

interface ImportResult {
  success: boolean
  message: string
  imported_count?: number
  errors?: string[]
}

export const ImportExport = () => {
  const [importing, setImporting] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleExportProducts = async () => {
    try {
      setExporting(true)
      setError(null)
      
      const response = await apiClient.getProducts()
      const products = response.products || response.data?.products || []

      // Convert to CSV format
      const headers = [
        'Name', 'Description', 'Category', 'Price', 'Cost Price', 
        'Stock Quantity', 'Min Stock Level', 'SKU', 'Barcode', 
        'Unit', 'GST Rate', 'Active'
      ]
      
      const csvData = [
        headers.join(','),
        ...products.map(product => [
          `"${product.name}"`,
          `"${product.description || ''}"`,
          `"${product.category_name}"`,
          product.price,
          product.cost_price || '',
          product.stock_quantity,
          product.min_stock_level || 10,
          `"${product.sku || ''}"`,
          `"${product.barcode || ''}"`,
          `"${product.unit || 'piece'}"`,
          product.gst_rate || 18,
          product.is_active ? 'Yes' : 'No'
        ].join(','))
      ].join('\n')

      // Create and download file
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `products_export_${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export products')
    } finally {
      setExporting(false)
    }
  }


  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Please select a CSV file')
      return
    }

    try {
      setImporting(true)
      setError(null)
      setImportResult(null)

      const text = await file.text()
      const lines = text.split('\n').filter(line => line.trim())
      
      if (lines.length < 2) {
        setError('CSV file must contain at least a header row and one data row')
        return
      }

      const headers = lines[0]?.split(',').map(h => h.replace(/"/g, '').trim().toLowerCase()) || []
      const dataRows = lines.slice(1).filter(line => line && line.trim())

      // Check if it's a products file
      const isProductsFile = headers.includes('name') && (headers.includes('price') || headers.includes('stock quantity'))

      if (!isProductsFile) {
        setError('Unrecognized CSV format. Please use the products template.')
        return
      }

      let importCount = 0
      let createdCount = 0
      let updatedCount = 0
      const errors: string[] = []

      if (isProductsFile) {
        // Import products with upsert logic
        const categoryResponse = await apiClient.getCategories()
        let categories = categoryResponse.categories || categoryResponse.data?.categories || []

        // Track created categories to avoid duplicates (case-insensitive)
        const categoryMap = new Map(categories.map(c => [c.name.toLowerCase().trim(), c]))

        // Get available units
        const unitsResponse = await apiClient.getUnits()
        const availableUnits = unitsResponse.units || unitsResponse.data?.units || []
        const unitMap = new Map(availableUnits.map(u => [u.short_name.toLowerCase().trim(), u]))

        for (let i = 0; i < dataRows.length; i++) {
          const row = dataRows[i]
          if (!row) continue
          const values = row.split(',').map(v => v.replace(/"/g, '').trim())

          try {
            const nameIndex = headers.indexOf('name')
            const categoryIndex = headers.indexOf('category')
            const priceIndex = headers.indexOf('price')
            const stockIndex = headers.indexOf('stock quantity')
            const skuIndex = headers.indexOf('sku')

            if (nameIndex === -1 || !values[nameIndex]) {
              errors.push(`Row ${i + 2}: Missing product name`)
              continue
            }

            if (categoryIndex === -1 || !values[categoryIndex]) {
              errors.push(`Row ${i + 2}: Missing category`)
              continue
            }

            // Find or create category (case-insensitive with trim)
            const categoryName = values[categoryIndex].trim()
            const categoryKey = categoryName.toLowerCase().trim()
            let category = categoryMap.get(categoryKey)

            if (!category) {
              // Create new category
              try {
                const newCategoryResponse = await apiClient.createCategory({
                  name: categoryName,
                  description: `Auto-created from import`,
                  is_active: true
                })
                category = newCategoryResponse.category || newCategoryResponse.data?.category
                categoryMap.set(categoryKey, category)
                categories.push(category)
              } catch (catErr) {
                errors.push(`Row ${i + 2}: Failed to create category "${categoryName}"`)
                continue
              }
            }

            // Find or create unit (case-insensitive with trim)
            const unitValue = (values[headers.indexOf('unit')] || 'piece').trim().toLowerCase()
            const unitKey = unitValue.toLowerCase().trim()
            let unit = unitMap.get(unitKey)

            if (!unit) {
              // Create new unit only if it doesn't exist
              try {
                const newUnitResponse = await apiClient.createUnit({
                  name: unitValue.charAt(0).toUpperCase() + unitValue.slice(1), // Capitalize first letter
                  short_name: unitValue,
                  description: `Auto-created from import`,
                  allow_decimal: 1, // Allow decimals by default
                  is_active: true
                })
                unit = newUnitResponse.unit || newUnitResponse.data?.unit
                if (unit) {
                  unitMap.set(unitKey, unit)
                }
              } catch (unitErr) {
                // Unit creation failed - likely because it already exists but wasn't in our initial list
                // Fetch units again to get the updated list
                try {
                  const refreshedUnitsResponse = await apiClient.getUnits()
                  const refreshedUnits = refreshedUnitsResponse.units || refreshedUnitsResponse.data?.units || []
                  const foundUnit = refreshedUnits.find(u => u.short_name.toLowerCase().trim() === unitKey)
                  if (foundUnit) {
                    unitMap.set(unitKey, foundUnit)
                  }
                } catch (refreshErr) {
                  // Just continue - the unit value will still be used in product data
                }
              }
            }

            // Normalize SKU - trim whitespace
            const skuValue = (values[skuIndex] || '').trim()

            const productData = {
              name: values[nameIndex],
              description: values[headers.indexOf('description')] || '',
              category_id: category.id,
              price: values[priceIndex] || '0',
              cost_price: values[headers.indexOf('cost price')] || '',
              stock_quantity: values[stockIndex] || '0',
              min_stock_level: values[headers.indexOf('min stock level')] || '10',
              sku: skuValue,
              barcode: (values[headers.indexOf('barcode')] || '').trim(),
              unit: unitValue,
              gst_rate: values[headers.indexOf('gst rate')] || '18',
              is_active: (values[headers.indexOf('active')] || 'yes').toLowerCase() === 'yes'
            }

            // Check if product exists by SKU (if SKU is provided)
            let existingProduct = null
            if (skuValue) {
              const searchResult = await apiClient.searchProductByCode(skuValue, true)
              existingProduct = searchResult.product
            }

            if (existingProduct) {
              // Update existing product
              await apiClient.updateProduct(existingProduct.id, productData)
              updatedCount++
            } else {
              // Create new product
              await apiClient.createProduct(productData)
              createdCount++
            }

            importCount++
          } catch (err) {
            errors.push(`Row ${i + 2}: ${err instanceof Error ? err.message : 'Import failed'}`)
          }
        }
      }

      const summaryParts = []
      if (createdCount > 0) summaryParts.push(`${createdCount} created`)
      if (updatedCount > 0) summaryParts.push(`${updatedCount} updated`)
      const summary = summaryParts.length > 0 ? ` (${summaryParts.join(', ')})` : ''

      setImportResult({
        success: true,
        message: `Successfully imported ${importCount} products${summary}`,
        imported_count: importCount,
        ...(errors.length > 0 && { errors })
      })

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import file')
    } finally {
      setImporting(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const downloadTemplate = () => {
    const headers = [
      'Name', 'Description', 'Category', 'Price', 'Cost Price',
      'Stock Quantity', 'Min Stock Level', 'SKU', 'Barcode',
      'Unit', 'GST Rate', 'Active'
    ]
    const sampleData = [
      '"Sample Product"', '"Product description"', '"Electronics"', '100.00', '80.00',
      '50', '10', '"SAMPLE001"', '"1234567890"', '"piece"', '18', 'Yes'
    ]

    const csvData = [
      headers.join(','),
      sampleData.join(',')
    ].join('\n')

    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', 'products_template.csv')
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Import & Export Products</h1>
        <p className="text-gray-600">Bulk import and export your product inventory</p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Import Result */}
      {importResult && (
        <div className={`border rounded-md p-4 ${
          importResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-center">
            <div className={`mr-3 ${importResult.success ? 'text-green-600' : 'text-red-600'}`}>
              {importResult.success ? '✅' : '❌'}
            </div>
            <div>
              <p className={`font-medium ${
                importResult.success ? 'text-green-800' : 'text-red-800'
              }`}>
                {importResult.message}
              </p>
              {importResult.errors && importResult.errors.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm text-red-600 font-medium">Errors:</p>
                  <ul className="text-sm text-red-600 list-disc list-inside mt-1 max-h-32 overflow-y-auto">
                    {importResult.errors.slice(0, 10).map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                    {importResult.errors.length > 10 && (
                      <li>... and {importResult.errors.length - 10} more errors</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Import Instructions - Prominent at top */}
      <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-6">
        <div className="flex items-start">
          <div className="text-blue-600 text-2xl mr-4">📋</div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-blue-900 mb-3">How Product Import Works</h3>
            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div className="bg-white rounded-md p-4 border border-blue-200">
                <h4 className="font-semibold text-blue-800 mb-2 flex items-center">
                  <span className="mr-2">🔄</span> Insert & Update
                </h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Products with matching SKU are <strong>updated</strong></li>
                  <li>• Products with new SKU are <strong>created</strong></li>
                  <li>• Both active and inactive products can be updated</li>
                </ul>
              </div>
              <div className="bg-white rounded-md p-4 border border-blue-200">
                <h4 className="font-semibold text-blue-800 mb-2 flex items-center">
                  <span className="mr-2">✨</span> Auto-Create Categories
                </h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Missing categories are created automatically</li>
                  <li>• No need to pre-create categories</li>
                  <li>• Case-insensitive matching</li>
                </ul>
              </div>
              <div className="bg-white rounded-md p-4 border border-blue-200">
                <h4 className="font-semibold text-blue-800 mb-2 flex items-center">
                  <span className="mr-2">📏</span> Auto-Create Units
                </h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Missing units are created automatically</li>
                  <li>• Use any unit name (kg, ltr, box, etc.)</li>
                  <li>• Decimals allowed by default</li>
                </ul>
              </div>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
              <p className="text-sm text-yellow-800 font-medium mb-1">⚠️ Important Notes:</p>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• <strong>SKU is the unique identifier</strong> - case-insensitive, ensure it's filled for updates</li>
                <li>• <strong>Categories & Units</strong> - case-insensitive, auto-created if missing</li>
                <li>• <strong>Whitespace</strong> - automatically trimmed from SKU, barcode, category, unit</li>
                <li>• Use quotes around text fields containing commas</li>
                <li>• Active column: use "Yes" or "No"</li>
                <li>• Price and quantity fields must be numeric</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Import Section */}
      <div className="bg-white rounded-lg shadow-lg border-2 border-orange-300 p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
          <span className="text-2xl mr-2">📥</span> Import Products
        </h2>
        <p className="text-gray-600 mb-6">
          Upload a CSV file to bulk import or update your products. Products are matched by SKU.
        </p>

        {/* File Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select CSV File
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleImportFile}
            disabled={importing}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-3 file:px-6 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-orange-500 file:text-white hover:file:bg-orange-600 file:cursor-pointer disabled:file:bg-gray-300"
          />
          <p className="text-xs text-gray-500 mt-1">
            Only CSV files are supported. Maximum file size: 5MB
          </p>
        </div>

        {importing && (
          <div className="flex items-center justify-center p-4 bg-orange-50 rounded-md">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500 mr-3"></div>
            <span className="text-orange-600 font-medium">Importing products...</span>
          </div>
        )}

        {/* Template Download */}
        <div className="border-t pt-6">
          <h3 className="font-medium text-gray-800 mb-3">CSV Template</h3>
          <p className="text-sm text-gray-600 mb-4">
            Download this template to ensure your CSV file has the correct format and column headers:
          </p>

          <button
            onClick={downloadTemplate}
            className="flex items-center justify-center py-3 px-6 border-2 border-orange-300 rounded-md text-sm font-semibold text-orange-700 bg-orange-50 hover:bg-orange-100 transition-colors"
          >
            <span className="text-lg mr-2">📦</span> Download Products Template
          </button>
        </div>
      </div>

      {/* Export Section */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
          <span className="text-2xl mr-2">📤</span> Export Products
        </h2>
        <p className="text-gray-600 mb-6">
          Download your current product inventory as a CSV file for backup, analysis, or editing.
        </p>

        <button
          onClick={handleExportProducts}
          disabled={exporting}
          className="w-full md:w-auto bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white py-3 px-6 rounded-md transition-colors font-medium"
        >
          {exporting ? 'Exporting...' : 'Export All Products'}
        </button>
      </div>
    </div>
  )
}